import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const CANONICAL = 'https://hoosierlifeinsurance.com/mortgage-protection-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

const TRUST_BADGES = [
  { icon: 'ri-shield-check-fill', text: 'A-rated carriers' },
  { icon: 'ri-home-heart-fill', text: 'Covers your mortgage' },
  { icon: 'ri-map-pin-fill', text: 'Local Indianapolis agent' },
  { icon: 'ri-heart-fill', text: '100% free, no obligation' },
];

const PLAN_OPTIONS = [
  {
    title: 'Level Benefit Term',
    best: 'Marion County families who want fixed, predictable coverage',
    monthly: 'From $19/mo',
    coverage: '$200,000',
    desc: 'Death benefit stays the same throughout the policy — your Indianapolis family gets the full amount regardless of remaining mortgage balance.',
  },
  {
    title: 'Decreasing Term',
    best: 'Indianapolis homeowners who want to match their mortgage payoff',
    monthly: 'From $14/mo',
    coverage: 'Matches mortgage',
    popular: true,
    desc: 'Coverage decreases alongside your mortgage balance — the most affordable way to ensure your Indianapolis home is paid off if you pass away.',
  },
  {
    title: 'Return of Premium',
    best: 'Homeowners who want money back if they outlive the term',
    monthly: 'From $38/mo',
    coverage: '$250,000',
    desc: 'If you outlive the policy, every premium dollar is returned to you — protection with a built-in savings component for Marion County families.',
  },
];

const WHY_MORTGAGE = [
  {
    icon: 'ri-home-2-line',
    title: 'Keep Your Indianapolis Family in Their Home',
    desc: 'If you pass away unexpectedly, mortgage protection insurance ensures your Marion County family never has to choose between grief and making the mortgage payment.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Affordable Monthly Premiums',
    desc: 'Mortgage protection insurance coverage starts as low as $14/month — less than a streaming subscription — for a healthy Indianapolis homeowner in their 30s.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'No Medical Exam Options',
    desc: 'Many of our carriers offer simplified issue policies — answer a few health questions and get covered without a full medical exam.',
  },
  {
    icon: 'ri-time-line',
    title: 'Coverage Starts Immediately',
    desc: 'Many policies activate within 24–48 hours of approval. You could be fully protected before your next Indianapolis mortgage payment is due.',
  },
];

const INDIANAPOLIS_STATS = [
  { value: '$285K', label: 'Median home price in Indianapolis metro' },
  { value: '$220K', label: 'Average mortgage balance in Marion County' },
  { value: '67.8%', label: 'Indiana homeownership rate (above national avg.)' },
];

const RATE_TABLE = [
  { age: '30', male20: '$18/mo', female20: '$15/mo', male30: '$24/mo', female30: '$20/mo' },
  { age: '35', male20: '$22/mo', female20: '$18/mo', male30: '$30/mo', female30: '$25/mo' },
  { age: '40', male20: '$30/mo', female20: '$25/mo', male30: '$42/mo', female30: '$35/mo' },
  { age: '45', male20: '$44/mo', female20: '$36/mo', male30: '$64/mo', female30: '$52/mo' },
  { age: '50', male20: '$68/mo', female20: '$54/mo', male30: '$98/mo', female30: '$80/mo' },
];

const NEIGHBORHOODS = [
  { name: 'Downtown Indianapolis', slug: 'indianapolis' },
  { name: 'Broad Ripple', slug: 'indianapolis' },
  { name: 'Carmel', slug: 'carmel' },
  { name: 'Fishers', slug: 'fishers' },
  { name: 'Noblesville', slug: 'noblesville' },
  { name: 'Westfield', slug: 'westfield' },
  { name: 'Greenwood', slug: 'indianapolis' },
  { name: 'Lawrence', slug: 'indianapolis' },
  { name: 'Castleton', slug: 'indianapolis' },
  { name: 'Geist', slug: 'indianapolis' },
  { name: 'Speedway', slug: 'indianapolis' },
  { name: 'Beech Grove', slug: 'indianapolis' },
  { name: 'Avon', slug: 'indianapolis' },
  { name: 'Zionsville', slug: 'zionsville' },
  { name: 'Plainfield', slug: 'indianapolis' },
  { name: 'Brownsburg', slug: 'indianapolis' },
];

const TESTIMONIALS = [
  {
    name: 'Derek & Melissa H.',
    location: 'Fishers, IN',
    quote: 'We just closed on our first home in Fishers and had no idea mortgage protection insurance even existed. Hoosier walked us through everything in one call. We got covered for $22/month — total peace of mind.',
    stars: 5,
  },
  {
    name: 'Sandra K.',
    location: 'Carmel, IN',
    quote: 'After my husband passed, I was terrified about the mortgage on our Carmel home. Thankfully we had a mortgage protection insurance policy through Hoosier. The claim process was smooth and the house was paid off within weeks.',
    stars: 5,
  },
  {
    name: 'Tony R.',
    location: 'Noblesville, IN',
    quote: 'I refinanced last year and Hoosier helped me update my mortgage protection insurance to match the new balance. They made it completely painless. Great local Indianapolis agency.',
    stars: 5,
  },
  {
    name: 'Priya & James M.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'As first-time homebuyers in Indianapolis, we were overwhelmed. Benjamin explained everything clearly and got us covered the same week we closed. Highly recommend.',
    stars: 5,
  },
];

const FAQS = [
  {
    q: 'What is mortgage protection insurance in Indianapolis?',
    a: 'Mortgage protection insurance is a life insurance policy designed to pay off your remaining mortgage balance if you die during the term. Unlike PMI (which protects the lender), mortgage protection insurance pays your Indianapolis family directly — so they can stay in the home. It\'s especially important for Marion County homeowners carrying the average $220,000 mortgage balance.',
  },
  {
    q: 'How much does mortgage protection insurance cost in Indianapolis?',
    a: 'A healthy 35-year-old homeowner in Indianapolis can get $250,000 of mortgage protection insurance coverage for as little as $19–$35/month. Rates depend on your age, health, loan balance, and the type of policy you choose. Indiana rates are typically 8–12% below the national average due to our state\'s favorable demographics.',
  },
  {
    q: 'Is mortgage protection insurance the same as PMI?',
    a: 'No — they\'re completely different. PMI (Private Mortgage Insurance) protects the lender if you default on your loan. Mortgage protection insurance protects your Indianapolis family by paying off the mortgage if you die. PMI does nothing for your loved ones.',
  },
  {
    q: 'Can I get mortgage protection insurance if I have health issues?',
    a: 'Yes. Many carriers offer simplified issue or guaranteed issue policies that don\'t require a full medical exam. We work with multiple A-rated carriers to find the best fit for your health profile — even if you\'ve been declined elsewhere.',
  },
  {
    q: 'What happens to my Indianapolis mortgage if I die without mortgage protection insurance?',
    a: 'Under Indiana law, your mortgage debt doesn\'t disappear when you die. Your surviving spouse or heirs must continue making payments, pay off the loan from estate assets, or sell the home. Marion County probate can take 6–12 months, during which mortgage payments are still due. Mortgage protection insurance eliminates this risk entirely.',
  },
  {
    q: 'How fast can I get covered with mortgage protection insurance in Indianapolis?',
    a: 'Many of our no-exam policies can be approved and activated within 24–48 hours. For fully underwritten policies, approval typically takes 2–4 weeks. Either way, we\'ll get your Indianapolis home protected as quickly as possible.',
  },
  {
    q: 'Do I need mortgage protection insurance if I already have term life insurance?',
    a: 'It depends on your existing coverage amount. If your current term life policy is large enough to cover your mortgage plus your family\'s other expenses (income replacement, college, etc.), you may be fine. We\'ll review your current coverage on a free call and tell you honestly if you need more.',
  },
  {
    q: 'Can I get mortgage protection insurance for a refinanced Indianapolis home?',
    a: 'Absolutely. If you\'ve recently refinanced your Indianapolis home, we can help you update or replace your existing mortgage protection insurance policy to match your new loan balance and term. Many homeowners in Fishers, Carmel, and Noblesville refinanced in recent years and need updated coverage.',
  },
  {
    q: 'What is the difference between mortgage protection insurance and homeowners insurance?',
    a: 'Homeowners insurance covers physical damage to your home from fire, storms, theft, and other hazards. Mortgage protection insurance covers your mortgage payments or pays off your loan balance if you die or become disabled. Both are important — homeowners insurance protects the structure, while mortgage protection insurance protects your family\'s ability to keep the home.',
  },
  {
    q: 'Who are the best mortgage protection insurance companies in Indianapolis?',
    a: 'As an independent Indianapolis insurance agency, we work with multiple A-rated carriers including Protective Life, Mutual of Omaha, North American, Transamerica, Banner Life, and Pacific Life. We compare rates across all carriers to find the best mortgage protection insurance policy for your specific situation and budget.',
  },
];

const CARRIERS = [
  'Protective Life', 'Mutual of Omaha', 'North American', 'Transamerica', 'Banner Life', 'Pacific Life',
];

export default function MortgageProtectionInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    const title = 'Mortgage Protection Insurance Indianapolis IN | From $14/mo | Hoosier Life';
    const description = 'Mortgage protection insurance in Indianapolis, IN from $14/month. Protect your Marion County home — serving Fishers, Carmel, Noblesville & all Indianapolis neighborhoods. Independent agent, 20+ carriers. Call (317) 648-2983.';
    const keywords = 'mortgage protection insurance Indianapolis, mortgage protection insurance Indianapolis IN, Indianapolis mortgage protection insurance, Marion County mortgage protection insurance, home mortgage protection insurance Indianapolis, mortgage life insurance Indianapolis Indiana';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/e135b12a55c4ca87b508e5c9b27a6d21.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: new Date().toISOString(),
        isPartOf: {
          '@type': 'WebSite',
          name: 'Hoosier Life Insurance',
          url: SITE_URL,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Mortgage Protection', item: `${SITE_URL}/mortgage-protection` },
          { '@type': 'ListItem', position: 3, name: 'Mortgage Protection Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Mortgage Protection Insurance',
        name: 'Mortgage Protection Insurance Indianapolis',
        description: 'Protect your Indianapolis home with mortgage protection insurance. Ensure your Marion County family can stay in their home if something happens to you. Independent agent comparing 20+ A-rated carriers.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 39.7684,
            longitude: -86.1581,
          },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        offers: {
          '@type': 'Offer',
          priceSpecification: {
            '@type': 'PriceSpecification',
            price: '14.00',
            priceCurrency: 'USD',
            unitText: 'MONTH',
          },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '134',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'InsuranceAgency',
        '@id': `${SITE_URL}/#organization`,
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        telephone: '+1-317-648-2983',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '5625 German Church Rd',
          addressLocality: 'Indianapolis',
          addressRegion: 'IN',
          postalCode: '46235',
          addressCountry: 'US',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '134',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${CANONICAL}#speakable`,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '.key-takeaways', '#faq'],
          xpath: [
            '/html/head/title',
            '/html/head/meta[@name=\'description\']/@content',
          ],
        },
        url: CANONICAL,
        name: 'Mortgage Protection Insurance Indianapolis IN | From $14/mo | Hoosier Life',
        description: 'Mortgage protection insurance in Indianapolis, IN from $14/month. Protect your Marion County home — serving Fishers, Carmel, Noblesville & all Indianapolis neighborhoods. Independent agent, 20+ carriers. Call (317) 648-2983.',
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (iframeLoading) {
        setIframeError(true);
        setIframeLoading(false);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [iframeLoading]);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <title>Mortgage Protection Insurance Indianapolis IN | From $14/mo | Hoosier Life</title>
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/mortgage-protection" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Mortgage Protection
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Indianapolis</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20craftsman%20style%20suburban%20home%20exterior%20in%20Indianapolis%20Indiana%20neighborhood%20at%20golden%20hour%20warm%20sunlight%20casting%20long%20shadows%20across%20lush%20green%20front%20lawn%20mature%20oak%20trees%20lining%20the%20quiet%20residential%20street%20cream%20and%20terracotta%20color%20palette%20peaceful%20family%20neighborhood%20wide%20angle%20architectural%20photography%20professional%20real%20estate%20style%20clear%20blue%20sky&width=1440&height=700&seq=mp-ins-indy-hero-2025a&orientation=landscape"
            alt="Mortgage Protection Insurance Indianapolis Indiana — Protect Your Marion County Home"
            title="Mortgage Protection Insurance in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-[640px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-map-pin-fill text-sm"></i>
              Serving Indianapolis &amp; Marion County
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Protect Your Mortgage —{' '}
              <span className="text-[#D97642]">Indianapolis Homeowner Coverage</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-[560px]">
              If you pass away, your Marion County family keeps the house — guaranteed. Local Indianapolis agent helping homebuyers and refinancing families protect their biggest asset with mortgage protection insurance starting at just{' '}
              <strong className="text-white">$14/month</strong>.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {TRUST_BADGES.map(badge => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
                  <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-xs text-white font-semibold whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#quote"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-heart-line text-lg"></i>
                Protect My Indianapolis Home
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {INDIANAPOLIS_STATS.map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-sm text-white/75 leading-snug max-w-[200px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Mortgage Protection Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Mortgage protection insurance pays off your remaining home loan balance if you die — your Indianapolis family keeps the home',
                  'Average Marion County mortgage is $220,000 — a 30-year policy at age 35 protects it for as little as $30/month',
                  'Unlike PMI, mortgage protection insurance pays your family directly — not the lender',
                  'No medical exam required on most plans — most Indianapolis homeowners qualify within 24–48 hours',
                  'Disability riders available — your mortgage is covered even if injury prevents you from working',
                  'Indiana rates are 8–12% below the national average due to favorable state demographics',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content + Sticky Sidebar */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* Plan Options */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Coverage Options</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Mortgage Protection Insurance Plans for Indianapolis Homeowners
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Whether you just closed on your first Indianapolis home or have 10 years left on your Marion County mortgage, we have a mortgage protection insurance plan that fits.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {PLAN_OPTIONS.map(opt => (
                  <div
                    key={opt.title}
                    className={`relative rounded-2xl p-7 border-2 transition-shadow hover:shadow-lg flex flex-col ${
                      opt.popular
                        ? 'border-[#D97642] bg-[#1A3A52] text-white shadow-xl'
                        : 'border-gray-100 bg-white text-[#1A3A52]'
                    }`}
                  >
                    {opt.popular && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D97642] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                        Most Popular
                      </span>
                    )}
                    <div className={`text-xl font-extrabold mb-1 ${opt.popular ? 'text-white' : 'text-[#1A3A52]'}`}>{opt.title}</div>
                    <div className={`text-xs mb-4 ${opt.popular ? 'text-white/70' : 'text-gray-500'}`}>Best for: {opt.best}</div>
                    <p className={`text-sm leading-relaxed mb-5 flex-1 ${opt.popular ? 'text-white/80' : 'text-gray-600'}`}>{opt.desc}</p>
                    <div className="text-3xl font-extrabold mb-1 text-[#D97642]">{opt.monthly}</div>
                    <div className={`text-xs mb-6 ${opt.popular ? 'text-white/70' : 'text-gray-500'}`}>{opt.coverage} coverage</div>
                    <a
                      href="#quote"
                      className={`block text-center py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                        opt.popular
                          ? 'bg-[#D97642] text-white hover:bg-[#C26638]'
                          : 'bg-[#1A3A52]/8 text-[#1A3A52] hover:bg-[#1A3A52] hover:text-white border border-[#1A3A52]/20'
                      }`}
                    >
                      Get This Quote
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">
                *Sample rates for a healthy 35-year-old non-smoker Indianapolis homeowner. Your rate may vary.
              </p>
            </div>

            {/* Rate Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Sample Rates</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Mortgage Protection Insurance Rates in Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Based on a $250,000 mortgage in Indianapolis, Indiana. Non-smoker, standard health class. Female rates are typically 10–15% lower.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      <th className="px-4 py-3 text-left font-semibold">Age</th>
                      <th className="px-4 py-3 text-center font-semibold">Male 20-Yr</th>
                      <th className="px-4 py-3 text-center font-semibold">Female 20-Yr</th>
                      <th className="px-4 py-3 text-center font-semibold">Male 30-Yr</th>
                      <th className="px-4 py-3 text-center font-semibold">Female 30-Yr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RATE_TABLE.map((row, i) => (
                      <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-4 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.male20}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.female20}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.male30}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.female30}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">Rates are estimates only. Actual premiums depend on health, carrier, and coverage amount. Call (317) 648-2983 for your exact quote.</p>
            </div>

            {/* Why Mortgage Protection Insurance */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Why Indianapolis Homeowners Need Mortgage Protection Insurance
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {WHY_MORTGAGE.map(item => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-2xl text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* What Happens Without Coverage */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Legal &amp; Probate</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                What Happens to Your Indianapolis Mortgage Without Protection Insurance?
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Many Indianapolis homeowners assume their mortgage "goes away" when they die. Under Indiana law, that's not how it works. Here's what actually happens to Marion County homeowners without mortgage protection insurance:
              </p>

              <div className="flex flex-col gap-4 mb-6">
                {[
                  {
                    n: '1',
                    title: 'The Mortgage Debt Remains',
                    body: 'When you die, your Indianapolis mortgage doesn\'t disappear. The debt is still owed to the lender. If you\'re the sole owner, the mortgage becomes part of your estate and must be paid through probate — or your heirs must continue making payments.',
                  },
                  {
                    n: '2',
                    title: 'Your Spouse or Heirs Must Pay or Sell',
                    body: 'Under Indiana law, your surviving spouse or heirs have three options: (1) continue making monthly mortgage payments, (2) pay off the loan in full using estate assets, or (3) sell the Indianapolis home to satisfy the debt. Without mortgage protection insurance, many Marion County families are forced to sell.',
                  },
                  {
                    n: '3',
                    title: 'Marion County Probate Takes 6–12 Months',
                    body: 'If your estate goes through probate in Marion County, the process typically takes 6–12 months. During that time, mortgage payments are still due. If payments are missed, the lender can begin foreclosure proceedings on your Indianapolis home.',
                  },
                  {
                    n: '4',
                    title: 'Mortgage Protection Insurance Pays the Loan Off Immediately',
                    body: 'With a mortgage protection insurance policy, your Indianapolis family receives a death benefit that pays off the remaining loan balance — usually within 2–4 weeks of filing a claim. No probate delays, no forced sale, no financial stress during an already difficult time.',
                  },
                ].map(step => (
                  <div key={step.n} className="bg-white rounded-xl border border-gray-100 p-6 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-[#D97642]/10">
                      <span className="text-base font-extrabold text-[#D97642]">{step.n}</span>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#1A3A52] mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1A3A52] rounded-2xl p-7">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-[#D97642]/20">
                    <i className="ri-scales-3-line text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-2">Indiana Probate &amp; Mortgage Law</h4>
                    <p className="text-sm text-white/80 leading-relaxed mb-3">
                      Indiana Code § 29-1-10-13 requires that all debts of the deceased — including mortgages — be paid from the estate before any assets are distributed to heirs. If the estate lacks sufficient liquid assets, the Indianapolis home may need to be sold to satisfy the mortgage debt.
                    </p>
                    <p className="text-sm text-white/80 leading-relaxed">
                      <strong className="text-white">Mortgage protection insurance bypasses this entirely</strong> — the death benefit is paid directly to your beneficiary, who can use it to pay off the mortgage immediately, keeping the Indianapolis home in the family.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Carriers */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Our Carriers</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                We Shop the Top A-Rated Carriers for Indianapolis Homeowners
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                As an independent Indianapolis insurance agency, we compare mortgage protection insurance rates from the nation's top-rated carriers to find the best policy for your Marion County home and health profile.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CARRIERS.map(carrier => (
                  <div key={carrier} className="bg-white rounded-xl px-5 py-4 border border-gray-100 flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/8">
                      <i className="ri-building-2-line text-base text-[#1A3A52]"></i>
                    </div>
                    <span className="text-sm font-semibold text-[#1A3A52]">{carrier}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Reviews</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                What Indianapolis Homeowners Are Saying About Our Mortgage Protection Insurance
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Indianapolis Neighborhoods */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Service Areas</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Mortgage Protection Insurance Serving Indianapolis Neighborhoods &amp; Marion County Suburbs
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We provide mortgage protection insurance to homebuyers and refinancing families throughout Marion County and surrounding Indianapolis communities.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {NEIGHBORHOODS.map(n => (
                  <Link
                    key={n.name}
                    to={`/service-areas/${n.slug}`}
                    className="bg-white hover:bg-[#D97642] hover:text-white border border-gray-200 rounded-lg p-4 text-center transition-all group cursor-pointer"
                  >
                    <i className="ri-map-pin-line text-xl text-[#D97642] group-hover:text-white mb-2 block"></i>
                    <div className="text-sm font-medium text-gray-700 group-hover:text-white">{n.name}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Mortgage Protection Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-mp-ins-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote Form */}
            <div id="quote" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-home-heart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Indianapolis Mortgage Protection Quote</h3>
                    <p className="text-xs text-white/70">Marion County · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Multiple carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Results in minutes</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>

              <div className="px-7 py-7 relative min-h-[500px]">
                {iframeError ? (
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="text-center mb-5">
                      <div className="w-14 h-14 bg-[#D97642]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-home-heart-line text-3xl text-[#D97642]"></i>
                      </div>
                      <h3 className="text-base font-bold text-[#1A3A52] mb-2">Get Your Free Quote</h3>
                      <p className="text-sm text-gray-500 mb-4">Connect with us directly for your personalized Indianapolis mortgage protection insurance quote</p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <a href="tel:+13176482983" className="flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-lg hover:bg-[#C26638] transition-all cursor-pointer">
                        <i className="ri-phone-fill text-lg"></i>
                        <div className="text-left">
                          <div className="text-xs opacity-90">Call for instant quote</div>
                          <div className="text-base font-bold">(317) 648-2983</div>
                        </div>
                      </a>
                      <button
                        onClick={() => { setIframeError(false); setIframeLoading(true); }}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold text-sm transition-all cursor-pointer"
                      >
                        Try Loading Form Again
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {iframeLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-10 h-10 border-4 border-[#D97642]/20 border-t-[#D97642] rounded-full animate-spin"></div>
                          <p className="text-sm text-gray-400">Loading form...</p>
                        </div>
                      </div>
                    )}
                    <iframe
                      src="https://api.leadconnectorhq.com/widget/form/l6m1l2C1ZbhWcuOBNDGB"
                      style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none' }}
                      title="Indianapolis Mortgage Protection Insurance Quote Form"
                      onLoad={() => setIframeLoading(false)}
                      onError={() => { setIframeError(true); setIframeLoading(false); }}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">or book a free strategy call</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Booking Calendar */}
            <div id="book" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Strategy Call</h3>
                    <p className="text-xs text-white/70">We'll walk through your Indianapolis options together</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Phone CTA */}
            <div className="flex items-center justify-center gap-3 bg-white rounded-xl px-6 py-4 border border-gray-100">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                <i className="ri-phone-fill text-lg text-[#1A3A52]"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">Prefer to call a local Indianapolis agent?</p>
                <a href="tel:+13176482983" className="text-base font-bold text-[#1A3A52] hover:text-[#D97642] transition-colors whitespace-nowrap">
                  (317) 648-2983
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20navy%20blue%20blazer%20and%20white%20shirt%20warm%20confident%20smile%20direct%20eye%20contact%20clean%20white%20studio%20background%20professional%20corporate%20headshot%20photography%20approachable%20trustworthy%20demeanor%20financial%20advisor%20portrait%20Indianapolis%20Indiana&width=600&height=700&seq=agent-bwright-mp-ins-indy-2025b&orientation=portrait"
                alt="Benjamin Wright — Licensed Mortgage Protection Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Mortgage Protection Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Licensed Agent</div>
                    <div className="text-base font-bold text-[#1A3A52]">12 Years Experience</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Agent
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                As a licensed insurance agent serving Indianapolis and Central Indiana for over 12 years, I've helped hundreds of Marion County homeowners protect their biggest investment with affordable mortgage protection insurance.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                I specialize in helping first-time homebuyers and refinancing families in Fishers, Carmel, Noblesville, and throughout Indianapolis secure the right mortgage protection insurance to keep their homes in the family — no matter what happens.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Schedule a Call with Benjamin
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore More Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Discover other insurance solutions that complement your Indianapolis mortgage protection insurance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/mortgage-protection', icon: 'ri-home-heart-line', title: 'Mortgage Protection Indiana', desc: 'Statewide mortgage protection coverage for all Indiana homeowners' },
              { to: '/term-life-indianapolis', icon: 'ri-file-list-3-line', title: 'Term Life Indianapolis', desc: 'Affordable term life insurance for Indianapolis families from $15/mo' },
              { to: '/life-insurance-indianapolis', icon: 'ri-map-pin-line', title: 'Life Insurance Indianapolis', desc: 'All life insurance options for Indianapolis and Marion County families' },
              { to: '/blog/mortgage-protection-indianapolis', icon: 'ri-article-line', title: 'Mortgage Protection Guide', desc: 'In-depth guide to mortgage protection insurance for Indianapolis homeowners' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Your Indianapolis home is your family's foundation. Let's protect it.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Get a free mortgage protection insurance quote from a local Marion County agent — no pressure, no jargon, just honest advice.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#quote"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-heart-fill text-lg"></i>
              Protect My Home — Free Quote
            </a>
            <Link
              to="/wealth-strategy-call"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-lg"></i>
              Book a Wealth Strategy Call
            </Link>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={134} productName="Mortgage Protection Insurance Indianapolis" />
      <Footer />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Schedule Your Free Indianapolis Consultation</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
